import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { AiBadge } from './AiBadge';
import { useSaveWorkspaceAction, useWorkspaceActions } from './api';
import { Chip } from './Chip';
import { MoreActions } from './MoreActions';
import { SettingsForm } from './SettingsForm';
import { safeHref } from './markdown';
import { RangeBand } from './settings/controls/RangeBand';
import { optionLabel, toDisplay, unitLabel } from './settings/format';
import { canChange, initialValues, readableKey, resolveSettings } from './settings/resolve';
import { validateSettings, validateValue } from './settings/validate';
import { categoryTitle, permittedUseLabel, runsOnDemand, sectionsByCategory } from './states';
import { heading, hint, hover, ink, lead as leadClass, line, muted, page as pageBg, small, surface, title } from './tokens';
import type { ActionSetting, ResolvedSetting, WorkspaceEntry, WorkspaceAction } from './types';

/** One part of a page: a heading, a line about it, something beside them, and its content, all on the page's own colour. */
function Section({ label, blurb, aside, children }: { label: string; blurb?: ReactNode; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className={cn('flex flex-col gap-4 rounded-xl border p-5', line, pageBg)}>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h2 className={cn(heading, ink)}>{label}</h2>
          {blurb ? <p className={cn(small, muted)}>{blurb}</p> : null}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

/**
 * The actions of the selected workspace (`/actions`): those this deployment
 * grants it, under their categories, a card each (name, publisher, summary,
 * licence, permitted use, on or off), and, opened, its description and the
 * workspace's use of it: on or off, the values its runs start from, and what
 * a person may still change for one run. Its administrators change them,
 * within what TileRiver allows; everyone else reads them. Under them, More
 * actions: the ones anyone in the workspace may request.
 */
export function WorkspaceActionsPage() {
  const { current, ready } = useSelectedWorkspace();
  const page = useWorkspaceActions(current?.public_id);
  const [params, setParams] = useSearchParams();
  const actions = page.data?.actions ?? [];
  const sections = useMemo(() => sectionsByCategory(actions, (a) => a.category, (a) => a.name), [actions]);
  const selectedId = params.get('action');
  const selected = selectedId ? actions.find((m) => m.id === selectedId) : undefined;
  const name = page.data?.workspace.name ?? current?.name;

  if (ready && !current) {
    return (
      <div className="mx-auto w-full max-w-[960px] px-6 py-9 sm:px-7">
        <p className={cn('text-sm', muted)}>You are not in a workspace yet.</p>
      </div>
    );
  }

  // The list as wide as the shell's other pages; one action's settings at a reading width.
  return (
    <div className={cn('mx-auto w-full px-6 pt-9 pb-11 sm:px-7', selected && current ? 'max-w-[960px]' : 'max-w-[1440px]')}>
      {selected && current ? (
        <ActionDetail
          key={`${current.public_id}:${selected.id}@${selected.version}`}
          action={selected}
          workspaceId={current.public_id}
          workspaceName={name}
          canManage={page.data?.canManage === true}
          onBack={() => setParams({})}
        />
      ) : (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-1.5">
            <h1 className={cn(title, ink)}>Actions</h1>
            <p className={cn('text-sm', muted)}>
              {name ? `The actions ${name} can run on its images, and the values they run with.` : 'The actions this workspace can run on its images.'}
            </p>
          </div>
          {page.isLoading || !ready ? <Skeleton className="h-32 w-full" /> : null}
          {page.error ? (
            <p role="alert" className="text-sm text-destructive">
              {(page.error as Error).message}
            </p>
          ) : null}
          {page.isSuccess && actions.length === 0 ? <p className={cn('text-sm', muted)}>No actions are available for this workspace.</p> : null}
          {sections.map((s) => (
            <section key={s.title} className="flex flex-col gap-3">
              <div className="flex items-baseline gap-2">
                <h2 className={cn(heading, ink)}>{s.title}</h2>
                <span className={cn(small, hint)}>{s.entries.length === 1 ? '1 action' : `${s.entries.length} actions`}</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {s.entries.map((m) => (
                  <ActionCard key={m.id} action={m} onOpen={() => setParams({ action: m.id })} />
                ))}
              </div>
            </section>
          ))}
          {selectedId && page.isSuccess && !selected ? (
            <p className={cn('text-sm', muted)}>
              That action is not available. <Link to="?" className="underline">See all actions</Link>
            </p>
          ) : null}
          <MoreActions workspaceName={name} />
        </div>
      )}
    </div>
  );
}

function ActionCard({ action, onOpen }: { action: WorkspaceAction; onOpen: () => void }) {
  const use = permittedUseLabel(action.permittedUses);
  const on = action.workspace.enabled;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn('flex flex-col gap-2.5 rounded-xl border p-4 text-left transition-colors', line, surface, hover)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <div className={cn('flex items-center gap-2', heading, ink)}>
            <span className="min-w-0">{action.name}</span>
            {action.ai ? <AiBadge /> : null}
          </div>
          <div className={cn(small, muted)}>{[action.publisher, `Version ${action.version}`].filter(Boolean).join(' · ')}</div>
        </div>
        <Chip tone={on ? 'on' : 'plain'}>{on ? 'On' : 'Off'}</Chip>
      </div>
      {action.summary ? <p className={cn('line-clamp-3 text-sm leading-relaxed', muted)}>{action.summary}</p> : null}
      <div className="mt-auto flex flex-wrap gap-1.5">
        <Chip>{action.licence?.spdx}</Chip>
        {use ? <Chip tone="use">{use}</Chip> : null}
      </div>
    </button>
  );
}

const CARD_FIELDS: Array<[string, string]> = [
  ['intendedUse', 'Intended use'],
  ['intendedUsers', 'Intended users'],
  ['population', 'Images it suits'],
  ['trainingData', 'Training data'],
  ['performance', 'Performance'],
  ['limitations', 'Limitations'],
  ['maintenance', 'Maintenance'],
];

/** What a workspace says about runs, per setting: locked, or the range and options a run may use. */
export interface RunLimit {
  locked: boolean;
  minimum?: number;
  maximum?: number;
  enum?: unknown[];
}

/** The workspace's stored values, as a form starts from them. */
export function valuesOf(entries: Record<string, WorkspaceEntry>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(entries).filter(([, e]) => e.default !== undefined).map(([k, e]) => [k, e.default]));
}

/** The workspace's stored limits for runs, by setting. */
export function limitsOf(entries: Record<string, WorkspaceEntry>): Record<string, RunLimit> {
  return Object.fromEntries(
    Object.entries(entries).map(([k, e]) => [k, {
      locked: e.locked === true,
      ...(e.minimum !== undefined ? { minimum: e.minimum } : {}),
      ...(e.maximum !== undefined ? { maximum: e.maximum } : {}),
      ...(e.enum !== undefined ? { enum: e.enum } : {}),
    }]),
  );
}

/** A setting within the workspace's limits: what its value must fit. */
export function narrowed(s: ResolvedSetting, l: RunLimit | undefined): ResolvedSetting {
  return {
    ...s,
    minimum: l?.minimum ?? s.minimum,
    maximum: l?.maximum ?? s.maximum,
    ...(s.enum ? { enum: l?.enum ?? s.enum } : {}),
  };
}

/** Why the workspace's limits for a setting do not hold, or null. */
export function limitProblem(s: ResolvedSetting, l: RunLimit | undefined, value: unknown): string | null {
  if (!l || l.locked) return null;
  const n = narrowed(s, l);
  if (n.minimum != null && n.maximum != null && n.minimum > n.maximum) return 'The lowest a run may use is above the highest.';
  if (l.enum && l.enum.length === 0) return 'Leave at least one option for runs.';
  return validateValue(n, value) ? 'The value is outside what runs may use.' : null;
}

/**
 * The workspace's entries a save sends, replacing what is stored: for every
 * setting the workspace may change, its value where it differs from
 * TileRiver's, its lock, or the range and options a run may use where they
 * are narrower. A setting with none of those has no entry.
 */
export function entriesOf(
  settings: Record<string, ActionSetting>,
  values: Record<string, unknown>,
  limits: Record<string, RunLimit>,
): Record<string, WorkspaceEntry> {
  const out: Record<string, WorkspaceEntry> = {};
  for (const s of resolveSettings(settings)) {
    if (!canChange(s, 'workspace')) continue;
    const e: WorkspaceEntry = {};
    if (Object.prototype.hasOwnProperty.call(values, s.key) && values[s.key] !== s.default) e.default = values[s.key];
    const l = limits[s.key];
    if (l?.locked) e.locked = true;
    else if (l) {
      if (l.minimum !== undefined && l.minimum !== s.minimum) e.minimum = l.minimum;
      if (l.maximum !== undefined && l.maximum !== s.maximum) e.maximum = l.maximum;
      if (l.enum && s.enum && l.enum.length < s.enum.length) e.enum = s.enum.filter((o) => l.enum!.includes(o));
    }
    if (Object.keys(e).length) out[s.key] = e;
  }
  return out;
}

/** What on or off means for this action in the workspace (`where` names it), in a sentence. */
export function standingLine(action: Pick<WorkspaceAction, 'name' | 'triggers'>, enabled: boolean, where = 'this workspace'): string {
  const onImport = !action.triggers || action.triggers.includes('on-import');
  const onDemand = runsOnDemand(action);
  if (!enabled) {
    return `Off: nobody in ${where} can run ${action.name}${onImport ? ', and it does not run on new images' : ''}.`;
  }
  const how = [onImport ? 'it runs on every new image' : '', onDemand ? `people in ${where} can run it on an image` : ''].filter(Boolean).join(', and ');
  return how ? `On: ${how}, starting from the values below.` : 'On, starting from the values below.';
}

function ActionDetail({ action, workspaceId, workspaceName, canManage, onBack }: { action: WorkspaceAction; workspaceId: string; workspaceName: string | undefined; canManage: boolean; onBack: () => void }) {
  const stored = action.workspace.settings;
  const start = useMemo(() => initialValues(action.settings, valuesOf(stored)), [action, stored]);
  // TileRiver's values: the action's defaults under the deployment's configuration, which Reset returns to.
  const defaults = useMemo(() => initialValues(action.settings), [action.settings]);
  const [values, setValues] = useState(start);
  const [limits, setLimits] = useState<Record<string, RunLimit>>(() => limitsOf(stored));
  const [enabled, setEnabled] = useState(action.workspace.enabled);
  const [saved, setSaved] = useState(false);
  const save = useSaveWorkspaceAction();
  useEffect(() => setSaved(false), [values, limits, enabled]);

  const all = useMemo(() => resolveSettings(action.settings), [action.settings]);
  const limitErrors = Object.fromEntries(
    all.map((s) => [s.key, limitProblem(s, limits[s.key], values[s.key])] as const).filter((x): x is readonly [string, string] => !!x[1]),
  );
  const invalid = Object.keys(validateSettings(action.settings, values, 'workspace')).length > 0 || Object.keys(limitErrors).length > 0;
  const entries = entriesOf(action.settings, values, limits);
  // Nothing of the workspace's own: every value and limit is TileRiver's.
  const atDefaults = Object.keys(entries).length === 0;
  const use = permittedUseLabel(action.permittedUses);
  const card = CARD_FIELDS.filter(([k]) => action.modelCard?.[k]);
  const extra = Object.keys(action.modelCard ?? {}).filter((k) => !CARD_FIELDS.some(([f]) => f === k));
  const busy = !canManage || save.isPending;

  const submit = () => {
    if (invalid || !canManage) return;
    save.mutate(
      { workspaceId, actionId: action.id, change: { enabled, settings: entries } },
      { onSuccess: () => setSaved(true) },
    );
  };
  const reset = () => {
    setValues(defaults);
    setLimits({});
  };
  const setLimit = (s: ResolvedSetting, l: RunLimit) => setLimits({ ...limits, [s.key]: l });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <button type="button" onClick={onBack} className={cn('mb-1 inline-flex items-center gap-1 self-start text-sm', muted, 'hover:underline')}>
          <ArrowLeft className="h-4 w-4" />
          All actions
        </button>
        <h1 className={cn('flex flex-wrap items-center gap-2.5', title, ink)}>
          <span className="min-w-0">{action.name}</span>
          {action.ai ? <AiBadge /> : null}
        </h1>
        <p className={cn('text-sm', muted)}>
          {[action.publisher, `Version ${action.version}`, categoryTitle(action.category)].filter(Boolean).join(' · ')}
        </p>
        <div className="flex flex-wrap gap-1.5">
          <Chip>{action.licence?.spdx}</Chip>
          {use ? <Chip tone="use">{use}</Chip> : null}
        </div>
        {action.summary ? <p className={cn('mt-1 max-w-2xl text-sm leading-relaxed', ink)}>{action.summary}</p> : null}
      </div>

      <Section
        label={workspaceName ? `For ${workspaceName}` : 'For this workspace'}
        blurb={
          <>
            <span>{standingLine(action, enabled, workspaceName)}</span>{' '}
            <span>{canManage ? 'Choose what users may customize for a run.' : 'Only your workspace’s administrators can change these.'}</span>
          </>
        }
        aside={
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            label={enabled ? 'On' : 'Off'}
            ariaLabel="Turned on in this workspace"
            disabled={busy}
            className={cn('text-[13px] font-medium', enabled && 'text-emerald-700 dark:text-emerald-400')}
          />
        }
      >
        <SettingsForm
          settings={action.settings}
          mode="workspace"
          layout="rows"
          values={values}
          onChange={setValues}
          disabled={busy}
          lead={() => 'Default'}
          aside={(s) => <RunSwitch setting={s} limit={limits[s.key]} disabled={busy} onChange={(l) => setLimit(s, l)} />}
          extra={(s) => (
            <RunBand setting={s} limit={limits[s.key]} problem={action.workspace.problems[s.key]} error={limitErrors[s.key]} disabled={busy} onChange={(l) => setLimit(s, l)} />
          )}
        />
        {canManage ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={submit} disabled={invalid || save.isPending}>
              {save.isPending ? <Loader2 className="animate-spin" /> : null}
              Save
            </Button>
            <Button type="button" variant="ghost" onClick={reset} disabled={busy || atDefaults}>
              Reset to defaults
            </Button>
            {saved ? <span className={cn('text-sm', muted)}>Saved.</span> : null}
          </div>
        ) : null}
        {save.error ? (
          <p role="alert" className="text-sm text-destructive">
            {(save.error as Error).message}
          </p>
        ) : null}
      </Section>

      {card.length > 0 || extra.length > 0 ? (
        <Section label="About this action">
          <dl className="grid gap-x-6 gap-y-2.5 text-sm sm:grid-cols-[11rem_minmax(0,1fr)]">
            {[...card, ...extra.map((k) => [k, readableKey(k.replace(/([a-z])([A-Z])/g, '$1_$2'))] as [string, string])].map(([k, label]) => (
              <div key={k} className="contents">
                <dt className={muted}>{label}</dt>
                <dd className={cn('leading-relaxed', ink)}>{action.modelCard![k]}</dd>
              </div>
            ))}
          </dl>
        </Section>
      ) : null}

      {action.upstream?.name || action.licence ? (
        <Section label="Source and licence">
          <div className="flex flex-col gap-1.5 text-sm leading-relaxed">
            {action.upstream?.name ? (
              <p className={ink}>
                Built on{' '}
                {safeHref(action.upstream.url) ? (
                  <a href={safeHref(action.upstream.url)!} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                    {action.upstream.name}
                  </a>
                ) : (
                  action.upstream.name
                )}
                {action.upstream.version ? ` ${action.upstream.version}` : ''}.
              </p>
            ) : null}
            {action.upstream?.citation ? <p className={muted}>{action.upstream.citation}</p> : null}
            <p className={muted}>
              Licence {action.licence?.spdx}
              {action.licence?.commercial === false ? ', not for commercial use' : ''}.
            </p>
          </div>
        </Section>
      ) : null}
    </div>
  );
}

/**
 * At the right of a setting's title: whether users may customize it for a
 * run. Only where the workspace may set the setting and TileRiver lets runs
 * change it; otherwise there is no switch and nothing to say.
 */
function RunSwitch({ setting, limit, disabled, onChange }: { setting: ResolvedSetting; limit: RunLimit | undefined; disabled: boolean; onChange: (l: RunLimit) => void }) {
  if (!canChange(setting, 'workspace') || !canChange(setting, 'run')) return null;
  const current: RunLimit = limit ?? { locked: false };
  return (
    <Switch
      size="sm"
      className="shrink-0 text-[12.5px]"
      checked={!current.locked}
      onCheckedChange={(on) => onChange({ ...current, locked: !on })}
      label="Allow users to customize"
      disabled={disabled}
    />
  );
}

/**
 * Under a setting's control, on the same scale and the same length: the band
 * of values runs may use (a number with bounds) or the options they may
 * choose (a list), while people may change it; and a stored value a later
 * configuration no longer allows, said plainly.
 */
function RunBand({
  setting,
  limit,
  problem,
  error,
  disabled,
  onChange,
}: {
  setting: ResolvedSetting;
  limit: RunLimit | undefined;
  problem?: string;
  error?: string;
  disabled: boolean;
  onChange: (limit: RunLimit) => void;
}) {
  const open = canChange(setting, 'workspace') && canChange(setting, 'run') && !(limit?.locked === true);
  const current: RunLimit = limit ?? { locked: false };
  // A number with both bounds has a band; a list has options; anything else has neither.
  const bounds = (setting.type === 'number' || setting.type === 'integer') && setting.minimum != null && setting.maximum != null
    ? { low: current.minimum ?? setting.minimum, high: current.maximum ?? setting.maximum }
    : null;
  const options = Array.isArray(setting.enum) && setting.enum.length > 1 ? setting.enum : null;
  const id = `run-limit-${setting.key}`;
  const unit = unitLabel(setting);
  return (
    <>
      {problem ? <p className="text-xs text-amber-700 dark:text-amber-400">This setting {problem}.</p> : null}
      {open && bounds ? (
        <div className="flex max-w-2xl items-center gap-3">
          <span className={leadClass}>Customization range</span>
          <RangeBand id={id} setting={setting} low={bounds.low} high={bounds.high} disabled={disabled} onChange={(lo, hi) => onChange({ ...current, minimum: lo, maximum: hi })} />
          {/* The same shape as the number beside the value's slider, so the two tracks end together. */}
          <div className={cn('flex shrink-0 items-center gap-2 text-xs tabular-nums', muted)}>
            <span className="w-24 text-right">
              {toDisplay(setting, bounds.low)} – {toDisplay(setting, bounds.high)}
            </span>
            {unit ? <span className="text-xs">{unit}</span> : null}
          </div>
        </div>
      ) : null}
      {open && options ? (
        <div className="flex max-w-2xl items-start gap-3">
          <span className={cn(leadClass, 'pt-0.5')}>Customization options</span>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5" role="group" aria-label={`Options runs may choose for ${setting.ui.title}`}>
            {options.map((o) => {
              const allowed = current.enum ?? options;
              const on = allowed.includes(o);
              return (
                <button
                  key={String(o)}
                  type="button"
                  aria-pressed={on}
                  disabled={disabled}
                  onClick={() => onChange({ ...current, enum: on ? allowed.filter((x) => x !== o) : [...allowed, o] })}
                  className={cn(
                    'rounded-md border px-2 py-0.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                    line,
                    on ? cn('bg-[color:var(--tr-row,var(--accent))] font-medium', ink) : muted,
                  )}
                >
                  {optionLabel(o)}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </>
  );
}
